import { apiClient } from './client';

export interface FeeStructureItem {
  id: number;
  feeStructureId: number;
  paymentCategoryId: number;
  paymentCategory: { id: number; name: string; description: string | null };
  amount: string | null;
  firstYearAmount: string | null;
  laterYearAmount: string | null;
  semester1Amount: string | null;
  semester2Amount: string | null;
  /** Free-text two-option display (a real value can be a range, e.g.
   * "1500-3000 USD") — currently only used by Accommodation, but not
   * hard-coded to it; every other item leaves both null. */
  option1Value: string | null;
  option2Value: string | null;
  currency: { id: number; code: string; symbol: string | null };
  isRequired: boolean;
  paymentTiming: string | null;
  isPublished: boolean;
  sortOrder: number;
}

export interface FeeStructure {
  id: number;
  universityId: number;
  university: { id: number; name: string };
  courseId: number | null;
  course: { id: number; name: string } | null;
  intakeId: number | null;
  intake: { id: number; name: string; month: number; year: number } | null;
  version: string;
  label: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  createdBy: { id: number; firstName: string; lastName: string };
  items: FeeStructureItem[];
}

export const listFeeStructures = async (params: {
  universityId?: number;
  courseId?: number;
  intakeId?: number;
  status?: string;
}): Promise<FeeStructure[]> => {
  const { data } = await apiClient.get('/master-data/fee-structures', { params });
  return data.data;
};

export const getFeeStructure = async (id: number): Promise<FeeStructure> => {
  const { data } = await apiClient.get(`/master-data/fee-structures/${id}`);
  return data.data;
};

export const getFeeStructureUsage = async (id: number): Promise<{ applications: number }> => {
  const { data } = await apiClient.get(`/master-data/fee-structures/${id}/usage`);
  return data.data;
};

export const createFeeStructure = async (payload: {
  universityId: number;
  courseId?: number;
  intakeId?: number;
  label?: string;
}): Promise<FeeStructure> => {
  const { data } = await apiClient.post('/master-data/fee-structures', payload);
  return data.data;
};

export const duplicateFeeStructure = async (id: number, intakeId?: number): Promise<FeeStructure> => {
  const { data } = await apiClient.post(`/master-data/fee-structures/${id}/duplicate`, intakeId ? { intakeId } : {});
  return data.data;
};

export const setFeeStructureStatus = async (
  id: number,
  action: 'publish' | 'unpublish' | 'archive',
): Promise<FeeStructure> => {
  const { data } = await apiClient.post(`/master-data/fee-structures/${id}/${action}`);
  return data.data;
};

export interface FeeStructureItemPayload {
  paymentCategoryId: number;
  amount?: number;
  firstYearAmount?: number;
  laterYearAmount?: number;
  semester1Amount?: number;
  semester2Amount?: number;
  option1Value?: string;
  option2Value?: string;
  currencyId: number;
  isRequired?: boolean;
  paymentTiming?: string;
}

export const addFeeStructureItem = async (
  feeStructureId: number,
  payload: FeeStructureItemPayload,
): Promise<FeeStructureItem> => {
  const { data } = await apiClient.post(`/master-data/fee-structures/${feeStructureId}/items`, payload);
  return data.data;
};

export const updateFeeStructureItem = async (
  feeStructureId: number,
  itemId: number,
  payload: Partial<FeeStructureItemPayload>,
): Promise<FeeStructureItem> => {
  const { data } = await apiClient.patch(`/master-data/fee-structures/${feeStructureId}/items/${itemId}`, payload);
  return data.data;
};

export const setFeeStructureItemPublished = async (
  feeStructureId: number,
  itemId: number,
  published: boolean,
): Promise<FeeStructureItem> => {
  const { data } = await apiClient.post(
    `/master-data/fee-structures/${feeStructureId}/items/${itemId}/${published ? 'publish' : 'unpublish'}`,
  );
  return data.data;
};
